import { atomFamily } from "jotai/utils";
import { atomWithQuery } from "jotai-tanstack-query";
import {
  collection,
  getDocs,
  limit as limitFirebase,
  or,
  orderBy,
  query,
  where,
} from "firebase/firestore";
import { DateTime } from "luxon";
import { customFirestore } from "../firebase";
import { type RunData, type TestSuiteData } from "../utils/types";

// Selectable timeline windows. We always fetch the largest (so switching windows
// is an instant client-side filter, never a refetch); narrower windows are
// applied in-memory. `days` is also the relevance + cost bound on the scan.
export const WINDOW_OPTIONS: Array<{ label: string; days: number }> = [
  { label: "7 days", days: 7 },
  { label: "14 days", days: 14 },
  { label: "1 month", days: 30 },
];
export const DEFAULT_WINDOW_DAYS = 7;
const MAX_WINDOW_DAYS = Math.max(...WINDOW_OPTIONS.map((o) => o.days));

// Safety cap on suite docs fetched (cheap reads); the date window above is the
// real bound. Generous so a high-volume org doesn't miss in-window suites.
const SUITE_SCAN_LIMIT = 500;

const normalizePlatform = (platform?: string): string =>
  (platform ?? "").trim().toLowerCase();

const toSeconds = (value: unknown): number => {
  const n = Number(value ?? 0);
  return Number.isFinite(n) ? n : 0;
};

// A single day on which the issue was seen for a given build, linking to the
// most recent failed run of that day.
export interface IssueDateHit {
  dayLabel: string;
  // Failed runs of this test on this build + day.
  count: number;
  suiteId: string;
  runId: string;
  epoch: number;
}

// A build (version + build number) within an app where the issue appeared.
export interface IssueBuild {
  appVersionName?: string;
  appVersionCode?: string;
  latestEpoch: number;
  dates: IssueDateHit[];
}

// An app (icon/platform/name/bundle) where the issue appeared, with its builds.
export interface IssueApp {
  appKey: string; // `${bundle}:${platform}` — also the icon-lookup key
  platform: string;
  appName?: string;
  bundle?: string;
  latestEpoch: number;
  builds: IssueBuild[];
}

// A recurring failure, identified by test + error message, deduplicated across
// apps/builds/days.
export interface AggregatedIssue {
  key: string;
  testName: string;
  // Root-cause label when grouped by an offline cluster (issueClusterId). When
  // present it's the issue headline and the cluster may span several tests.
  clusterLabel?: string;
  // Distinct test names in this issue, most frequent first (>1 only when a
  // cluster spans tests).
  testNames: string[];
  distinctTests: number;
  // Most recent error message for this test.
  statusMessage: string;
  // Number of distinct (normalized) error messages seen; > 1 means the message
  // varied between runs (statusMessage shows the latest).
  errorVariantCount: number;
  // Representative failure report (most recent occurrence that has one), for
  // linking the error headline.
  hasReport: boolean;
  reportSuiteId?: string;
  reportRunId?: string;
  // Most recent occurrence's fix PR, if any.
  prUrl?: string | null;
  // Impact metrics.
  occurrenceCount: number;
  distinctApps: number;
  distinctBuilds: number;
  distinctDays: number;
  latestEpoch: number;
  // Where it was found, grouped by app then build, most recent first.
  apps: IssueApp[];
}

// A single failed run with its suite context. Returned raw by the atom so the
// page can window/filter/aggregate/sort entirely client-side.
export interface IssueOccurrence {
  issueKey: string;
  testName: string;
  clusterLabel?: string;
  statusMessage: string;
  platform: string;
  bundle?: string;
  appName?: string;
  appVersionName?: string;
  appVersionCode?: string;
  day: string;
  dayLabel: string;
  hasReport: boolean;
  prUrl?: string | null;
  suiteId: string;
  runId: string;
  epoch: number;
}

export type IssueSort = "mostFound" | "mostRecent";

export interface IssueFilter {
  platform?: string;
  bundle?: string;
  version?: string; // appVersionName
}

// A run is shown as an issue when the test genuinely failed: either the
// auto-review agent confirmed a real app bug, or the run finished in the
// "failed" status. Runs the agent classified as its own (agentic) failure are
// excluded as not legitimate, as are runs still running / scheduled / passed.
// Matches the canonical "failed" definition used by the Reports grid
// (reports-page.tsx computeRunCounts) so suites with no failing tests drop out.
const isDisplayableFailure = (run: RunData): boolean => {
  if (run.finalFailureClassification === "legitimate_app_issue") return true;
  if (run.finalFailureClassification === "agentic_failure_unresolved")
    return false;
  return run.status === "failed";
};

const normalizeText = (s: string): string =>
  s.trim().replace(/\s+/g, " ").toLowerCase();

// "The same issue" — identity used to deduplicate failures:
//   1. `issueClusterId` when present: a root-cause cluster assigned offline by
//      cluster_failure_issues.py (LLM clustering). Most accurate.
//   2. otherwise the normalized test name. We deliberately do NOT key on the
//      error message, which varies slightly run-to-run and would fragment one
//      issue into many. App-agnostic, so one issue can span multiple
//      apps/platforms (shown as separate app boxes); the headline uses the most
//      recent error message and `errorVariantCount` surfaces when they differ.
const issueKeyFor = (testName: string, issueClusterId?: string): string => {
  const clusterId = issueClusterId?.trim();
  if (clusterId) return clusterId;
  return normalizeText(testName);
};

// True when an occurrence matches the (optional) app filter.
export const matchesFilter = (o: IssueOccurrence, f: IssueFilter): boolean => {
  if (f.platform && o.platform !== f.platform) return false;
  if (f.bundle && (o.bundle ?? "") !== f.bundle) return false;
  if (f.version && (o.appVersionName ?? "") !== f.version) return false;
  return true;
};

// Aggregate (already-filtered) occurrences into issues, nested app -> build ->
// day, then sort. Pure — safe to call from a useMemo on every filter change.
//
// `prByKey` (optional) maps issueKey -> a fix-PR url derived from the FULL
// fetched data (ignoring the timeline window). The PR is stamped on the single
// run where the auto-review opened it, which may be older than the window; this
// lets an issue still surface its PR even when that run isn't in the window.
export const buildIssues = (
  occurrences: IssueOccurrence[],
  sort: IssueSort,
  prByKey?: Map<string, string>
): AggregatedIssue[] => {
  const byIssue = new Map<string, IssueOccurrence[]>();
  for (const occ of occurrences) {
    const list = byIssue.get(occ.issueKey);
    if (list) list.push(occ);
    else byIssue.set(occ.issueKey, [occ]);
  }

  const issues: AggregatedIssue[] = Array.from(byIssue.entries()).map(
    ([key, occs]) => {
      occs.sort((a, b) => b.epoch - a.epoch);
      const latest = occs[0];
      const reportOcc = occs.find((o) => o.hasReport);
      const prOcc = occs.find((o) => o.prUrl);
      const errorVariantCount = new Set(
        occs.map((o) => normalizeText(o.statusMessage)).filter(Boolean)
      ).size;

      // Distinct test names, most frequent first (a cluster can span tests).
      const testCounts = new Map<string, number>();
      for (const o of occs) {
        testCounts.set(o.testName, (testCounts.get(o.testName) ?? 0) + 1);
      }
      const testNames = Array.from(testCounts.entries())
        .sort((a, b) => b[1] - a[1])
        .map(([name]) => name);
      const clusterLabel = occs.find((o) => o.clusterLabel)?.clusterLabel;

      // Nest: app -> build -> day. Temp maps carry the latest run per leaf.
      interface BuildAcc {
        appVersionName?: string;
        appVersionCode?: string;
        latestEpoch: number;
        dates: Map<string, IssueDateHit>;
      }
      interface AppAcc {
        appKey: string;
        platform: string;
        appName?: string;
        bundle?: string;
        latestEpoch: number;
        builds: Map<string, BuildAcc>;
      }
      const appMap = new Map<string, AppAcc>();
      const buildSet = new Set<string>();
      const daySet = new Set<string>();

      for (const o of occs) {
        daySet.add(o.day);
        const appKey = `${o.bundle ?? ""}:${o.platform}`;
        let app = appMap.get(appKey);
        if (!app) {
          app = {
            appKey,
            platform: o.platform,
            appName: o.appName,
            bundle: o.bundle,
            latestEpoch: o.epoch,
            builds: new Map(),
          };
          appMap.set(appKey, app);
        }
        if (o.epoch > app.latestEpoch) {
          app.latestEpoch = o.epoch;
          app.appName = o.appName;
        }

        const bKey = `${o.appVersionName ?? ""}|${o.appVersionCode ?? ""}`;
        buildSet.add(`${appKey}|${bKey}`);
        let build = app.builds.get(bKey);
        if (!build) {
          build = {
            appVersionName: o.appVersionName,
            appVersionCode: o.appVersionCode,
            latestEpoch: o.epoch,
            dates: new Map(),
          };
          app.builds.set(bKey, build);
        }
        if (o.epoch > build.latestEpoch) build.latestEpoch = o.epoch;

        const hit = build.dates.get(o.day);
        if (hit) {
          hit.count += 1;
          if (o.epoch > hit.epoch) {
            hit.epoch = o.epoch;
            hit.suiteId = o.suiteId;
            hit.runId = o.runId;
            hit.dayLabel = o.dayLabel;
          }
        } else {
          build.dates.set(o.day, {
            dayLabel: o.dayLabel,
            count: 1,
            suiteId: o.suiteId,
            runId: o.runId,
            epoch: o.epoch,
          });
        }
      }

      const apps: IssueApp[] = Array.from(appMap.values())
        .sort((a, b) => b.latestEpoch - a.latestEpoch)
        .map((app) => ({
          appKey: app.appKey,
          platform: app.platform,
          appName: app.appName,
          bundle: app.bundle,
          latestEpoch: app.latestEpoch,
          builds: Array.from(app.builds.values())
            .sort((a, b) => b.latestEpoch - a.latestEpoch)
            .map((b) => ({
              appVersionName: b.appVersionName,
              appVersionCode: b.appVersionCode,
              latestEpoch: b.latestEpoch,
              dates: Array.from(b.dates.values()).sort(
                (x, y) => y.epoch - x.epoch
              ),
            })),
        }));

      return {
        key,
        testName: latest.testName,
        clusterLabel,
        testNames,
        distinctTests: testNames.length,
        statusMessage: latest.statusMessage,
        errorVariantCount,
        hasReport: reportOcc != null,
        reportSuiteId: reportOcc?.suiteId,
        reportRunId: reportOcc?.runId,
        prUrl: prOcc?.prUrl ?? prByKey?.get(key) ?? null,
        occurrenceCount: occs.length,
        distinctApps: appMap.size,
        distinctBuilds: buildSet.size,
        distinctDays: daySet.size,
        latestEpoch: latest.epoch,
        apps,
      };
    }
  );

  const compare =
    sort === "mostRecent"
      ? (a: AggregatedIssue, b: AggregatedIssue) =>
          b.latestEpoch - a.latestEpoch || b.occurrenceCount - a.occurrenceCount
      : (a: AggregatedIssue, b: AggregatedIssue) =>
          b.occurrenceCount - a.occurrenceCount ||
          b.distinctDays - a.distinctDays ||
          b.distinctBuilds - a.distinctBuilds ||
          b.latestEpoch - a.latestEpoch;

  return issues.sort(compare);
};

// Fetches the raw failed-run occurrences for the org over the max window. The
// page narrows by timeline/app-filter and aggregates/sorts in-memory.
const issueOccurrencesAtom = atomFamily((organizationId: string) => {
  return atomWithQuery<IssueOccurrence[]>(() => ({
    queryKey: ["getApiIssueOccurrences", organizationId, MAX_WINDOW_DAYS],
    refetchOnMount: false,
    refetchOnWindowFocus: false,
    queryFn: async () => {
      const suitesRef = collection(customFirestore, "custom-test-suite-runs");

      // Cut-off (epoch seconds) for the rolling window. Filtered client-side:
      // timeInEpoch can be stored as a string, so a Firestore numeric range
      // filter would match nothing, and this avoids a new composite index.
      const cutoffSeconds = Date.now() / 1000 - MAX_WINDOW_DAYS * 24 * 60 * 60;

      // Order + limit by recency; filter the window client-side to avoid a new
      // composite index on top of the org `or(...)` filter.
      const suitesSnap = await getDocs(
        query(
          suitesRef,
          or(
            where("organizationId", "==", organizationId),
            where("organisationId", "==", organizationId)
          ),
          orderBy("timeInEpoch", "desc"),
          limitFirebase(SUITE_SCAN_LIMIT)
        )
      );

      // All triggers are in scope — a legitimate failure is an issue regardless
      // of whether the run was started via API, web, upload or rerun.
      const windowSuites = suitesSnap.docs
        .map((d) => ({ ...(d.data() as TestSuiteData), id: d.id }))
        .filter((s) => toSeconds(s.timeInEpoch) >= cutoffSeconds);

      if (windowSuites.length === 0) return [];

      // Collect every failed run (with its suite context) as a flat occurrence
      // list. Per-suite arrays avoid races on shared state.
      const perSuite = await Promise.all(
        windowSuites.map(async (suite): Promise<IssueOccurrence[]> => {
          const runsSnap = await getDocs(
            collection(
              customFirestore,
              `custom-test-suite-runs/${suite.id}/runs`
            )
          );

          const epoch = toSeconds(suite.timeInEpoch);
          const day =
            DateTime.fromMillis(epoch * 1000).toISODate() ?? "unknown";
          const dayLabel = DateTime.fromMillis(epoch * 1000).toFormat(
            "LLL dd, yyyy"
          );
          const platform = normalizePlatform(suite.platform);

          return runsSnap.docs
            .map((d) => ({ ...(d.data() as RunData), id: d.id }))
            .filter(isDisplayableFailure)
            .map((run) => {
              const testName = run.test_title ?? run.title ?? "Untitled test";
              const statusMessage = run.statusMessage ?? "";
              return {
                issueKey: issueKeyFor(testName, run.issueClusterId),
                testName,
                clusterLabel: run.issueClusterLabel,
                statusMessage,
                platform,
                bundle: suite.bundle,
                appName: suite.app_name,
                appVersionName: suite.appVersionName,
                appVersionCode: suite.appVersionCode,
                day,
                dayLabel,
                hasReport: run.failureReport?.status === "completed",
                prUrl: run.failureCodeReview?.prUrl ?? null,
                suiteId: suite.id,
                runId: run.id,
                epoch:
                  run.finishedTimeInEpoch ?? run.startedTimeInEpoch ?? epoch,
              };
            });
        })
      );

      return perSuite.flat();
    },
  }));
});

export default issueOccurrencesAtom;
